import type { Role } from './roles';

/**
 * Authority / identity presentation — the SINGLE source of truth for how the
 * seven backend roles are shown in the UI. Mirrors the Executive Authority
 * Model: three derived tiers over the unchanged backend roles. The server
 * remains authoritative; this is presentation/gating only.
 *
 * Rules (binding):
 *  - Exactly three tiers: SUPER_ADMIN > ADMIN > EMPLOYEE.
 *  - The literal internal role strings ("superadmin", "manager", "team_lead",
 *    "bde", "developer", ...) are NEVER rendered in an authority context.
 *  - Owner identity always renders as "Durrani Uzef — CEO & Founder".
 */

export type AuthorityTier = 'SUPER_ADMIN' | 'ADMIN' | 'EMPLOYEE';

/** Derive the authority tier from a backend role. */
export function tierOf(role: Role | string | null | undefined): AuthorityTier {
  if (role === 'superadmin') return 'SUPER_ADMIN';
  if (role === 'admin') return 'ADMIN';
  // manager / team_lead / bde / developer / employee are all EMPLOYEE-tier.
  return 'EMPLOYEE';
}

/** True for the executive (owner) tier — gates hidden executive surfaces. */
export function isExecutive(role: Role | string | null | undefined): boolean {
  return tierOf(role) === 'SUPER_ADMIN';
}

interface IdentityInput {
  displayName?: string | null;
  role?: Role | string | null;
}

export interface ResolvedIdentity {
  tier: AuthorityTier;
  /** Short designation chip — safe to show anywhere (never an internal role). */
  designation: string;
  /** Full attribution label for "Assigned by / Created by / Approved by". */
  attribution: string;
}

const OWNER_ATTRIBUTION = 'Durrani Uzef — CEO & Founder';

/**
 * Resolve a user into tier-safe presentation labels.
 * - SUPER_ADMIN → "Durrani Uzef — CEO & Founder"
 * - ADMIN       → "Department Administrator"
 * - EMPLOYEE    → the person's display name (or "Employee")
 */
export function identityOf(user: IdentityInput | null | undefined): ResolvedIdentity {
  const tier = tierOf(user?.role);
  if (tier === 'SUPER_ADMIN') {
    return { tier, designation: 'CEO & Founder', attribution: OWNER_ATTRIBUTION };
  }
  if (tier === 'ADMIN') {
    return {
      tier,
      designation: 'Administrator',
      attribution: 'Department Administrator',
    };
  }
  return {
    tier,
    designation: 'Employee',
    attribution: user?.displayName?.trim() || 'Employee',
  };
}
